import type { CourseType } from "@/components/course-card";
import { GST_PRICE_NOTE } from "@/lib/site-config";

/** Instructor line shown on cards / detail — brochure-aligned. */
export const ACADEMY_INSTRUCTOR_LINE = "Professional Data Scientists";

const avatar =
  "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=150&auto=format&fit=crop";

export type CourseCatalogDetail = {
  id: string;
  title: string;
  subtitle: string;
  rating: number;
  reviews: number;
  studentsEnrolled: number;
  /** Primary INR price shown everywhere — brochure “One Time Payment” */
  priceMain: string;
  /** Optional brochure crossed/higher reference text */
  priceCompareNote?: string;
  /** EMI (3 months) — brochure wording */
  emi: string | null;
  level: CourseType["level"];
  duration: string;
  lastUpdated: string;
  image: string;
  whatYouWillLearn: string[];
  curriculum: { title: string; lessons: number; duration: string }[];
};

const RECORDS: CourseCatalogDetail[] = [
  {
    id: "360-data-science",
    title: "360 Data Science",
    subtitle:
      "Python programming, analytics & ML, Tableau/Power BI, SAS Base & Advance, MS SQL, and Advance Excel — full roadmap aligned with Mont2 brochure.",
    rating: 4.9,
    reviews: 1240,
    studentsEnrolled: 8450,
    priceMain: "₹44,999 + GST",
    priceCompareNote: "Orig. Amt ₹59,999",
    emi: "₹16,000 / month × 3 + GST (Orig. Amt ₹21,000 / month)",
    level: "Beginner to Pro",
    duration: "6 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Python essentials through analytics libraries",
      "Machine learning workflows end-to-end",
      "Visualization with Tableau and Power BI",
      "SAS Base & Advance plus SQL for analytics",
      "Advance Excel modelling skills",
    ],
    curriculum: [
      { title: "Python programming foundations", lessons: 18, duration: "42h" },
      { title: "Analytics & machine learning", lessons: 22, duration: "55h" },
      { title: "Visualization — Tableau & Power BI", lessons: 14, duration: "32h" },
      { title: "SAS Base & Advance", lessons: 16, duration: "36h" },
      { title: "SQL Server & Advance Excel capstones", lessons: 12, duration: "28h" },
    ],
  },
  {
    id: "data-analysts",
    title: "Data Analyst",
    subtitle:
      "Python programming, analytics, machine learning, Tableau/Power BI, and SQL — brochure-aligned blended analytics career track.",
    rating: 4.8,
    reviews: 850,
    studentsEnrolled: 6200,
    priceMain: "₹34,999 + GST",
    priceCompareNote: "Orig. Amt ₹40,000",
    emi: "₹12,500 / month × 3 + GST (Orig. Amt ₹14,333 / month)",
    level: "Intermediate",
    duration: "4–5 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Python for analytics workflows",
      "Machine learning modelling fundamentals",
      "Insight dashboards with Tableau/Power BI",
      "SQL for analysts",
    ],
    curriculum: [
      { title: "Python programming & libraries", lessons: 14, duration: "30h" },
      { title: "Predictive modelling with ML", lessons: 16, duration: "40h" },
      { title: "Visualization with Power BI/Tableau", lessons: 12, duration: "24h" },
      { title: "SQL & internship-style assignments", lessons: 10, duration: "20h" },
    ],
  },
  {
    id: "python-ml",
    title: "Python & Machine Learning",
    subtitle:
      "Python programming plus analytics and machine learning — accelerated brochure track.",
    rating: 4.8,
    reviews: 620,
    studentsEnrolled: 5100,
    priceMain: "₹19,999 + GST",
    emi: "₹7,500 / month × 3 + GST",
    level: "Intermediate",
    duration: "3–4 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Structured Python programming for analytics",
      "Exploratory data analysis workflows",
      "Core supervised ML algorithms and evaluation",
    ],
    curriculum: [
      { title: "Python essentials & tooling", lessons: 12, duration: "22h" },
      { title: "EDA & feature workflows", lessons: 10, duration: "18h" },
      { title: "Regression & classification models", lessons: 14, duration: "32h" },
      { title: "Assignments & case studies", lessons: 8, duration: "16h" },
    ],
  },
  {
    id: "business-analyst",
    title: "Business Analyst with Visualization",
    subtitle:
      "Tableau/Power BI, SQL, and Advance Excel — analyst-ready visualization stack.",
    rating: 4.9,
    reviews: 2100,
    studentsEnrolled: 7800,
    priceMain: "₹21,999 + GST",
    priceCompareNote: "Orig. Amt ₹25,000",
    emi: "₹8,500 / month × 3 + GST (Orig. Amt ₹9,500 / month)",
    level: "All Levels",
    duration: "2–4 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Dashboard storytelling with Tableau/Power BI",
      "SQL fundamentals for reporting",
      "Advance Excel modelling & automation concepts",
    ],
    curriculum: [
      { title: "Visualization foundations", lessons: 10, duration: "18h" },
      { title: "SQL for analysts", lessons: 12, duration: "22h" },
      { title: "Advance Excel analytics", lessons: 10, duration: "16h" },
      { title: "Business case workshops", lessons: 6, duration: "12h" },
    ],
  },
  {
    id: "sas-data-analyst",
    title: "SAS for Data Analyst",
    subtitle: "SAS Base + Advance SAS plus SQL — brochure-aligned analyst credentialing path.",
    rating: 4.7,
    reviews: 450,
    studentsEnrolled: 2100,
    priceMain: "₹14,999 + GST",
    emi: "₹6,000 / month × 3 + GST",
    level: "Intermediate",
    duration: "2–3 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1509228468518-180dd4864904?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "SAS programming fundamentals",
      "Advance SAS procedures",
      "SQL connectivity patterns used by analysts",
    ],
    curriculum: [
      { title: "SAS programming essentials", lessons: 14, duration: "26h" },
      { title: "Advance SAS modules", lessons: 12, duration: "24h" },
      { title: "SQL integration labs", lessons: 8, duration: "14h" },
    ],
  },
  {
    id: "tableau-viz",
    title: "Visualization Using Tableau",
    subtitle:
      "Tableau Desktop through dashboards — covers Tableau Server/Online concepts per brochure.",
    rating: 4.8,
    reviews: 1320,
    studentsEnrolled: 4300,
    priceMain: "₹9,999 + GST",
    emi: "₹4,500 / month × 3 + GST",
    level: "Beginner",
    duration: "1–2 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Connecting and shaping data in Tableau",
      "Calculated fields & interactive dashboards",
      "Publishing concepts for Server/Online",
    ],
    curriculum: [
      { title: "Desktop fundamentals", lessons: 8, duration: "14h" },
      { title: "Advanced visuals & maps", lessons: 8, duration: "14h" },
      { title: "Dashboard actions & polish", lessons: 6, duration: "10h" },
    ],
  },
  {
    id: "power-bi",
    title: "Visualization Using Power BI",
    subtitle:
      "Power BI, Power Query Editor, and Power BI Report Server workflows aligned with brochure outline.",
    rating: 4.8,
    reviews: 1150,
    studentsEnrolled: 3900,
    priceMain: "₹9,999 + GST",
    emi: "₹4,500 / month × 3 + GST",
    level: "Beginner",
    duration: "1–2 months",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Power Query transformations",
      "DAX fundamentals for analysts",
      "Interactive reporting & service basics",
    ],
    curriculum: [
      { title: "Power BI Desktop fundamentals", lessons: 9, duration: "16h" },
      { title: "Modeling & DAX essentials", lessons: 9, duration: "16h" },
      { title: "Publishing & collaboration patterns", lessons: 6, duration: "10h" },
    ],
  },
  {
    id: "sql-server",
    title: "SQL Server",
    subtitle:
      "MS SQL Server Management Studio skills tailored for data analysts — brochure duration window.",
    rating: 4.9,
    reviews: 890,
    studentsEnrolled: 5600,
    priceMain: "₹9,999 + GST",
    emi: "₹4,500 / month × 3 + GST",
    level: "Beginner",
    duration: "4–6 weeks",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1544383835-bda2bc66a55d?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Relational database foundations",
      "Complex querying using SSMS",
      "Preparation patterns for analytics roles",
    ],
    curriculum: [
      { title: "SQL syntax & filtering", lessons: 10, duration: "16h" },
      { title: "Joins, aggregates & subqueries", lessons: 12, duration: "18h" },
      { title: "Analyst-focused SQL labs", lessons: 8, duration: "12h" },
    ],
  },
  {
    id: "adv-excel",
    title: "Advance Excel",
    subtitle:
      "Advanced spreadsheet analytics — brochure lists one-time fee only (no EMI).",
    rating: 4.7,
    reviews: 2100,
    studentsEnrolled: 9100,
    priceMain: "₹4,999 + GST",
    emi: null,
    level: "All Levels",
    duration: "4–5 weeks",
    lastUpdated: "May 2026",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1200&auto=format&fit=crop",
    whatYouWillLearn: [
      "Advanced formulas & modelling",
      "Pivot analytics & dashboards",
      "Automation concepts for reporting",
    ],
    curriculum: [
      { title: "Formula mastery", lessons: 8, duration: "12h" },
      { title: "Pivot & visualization", lessons: 6, duration: "10h" },
      { title: "Case-driven assignments", lessons: 5, duration: "8h" },
    ],
  },
];

export function listCoursesForCards(): CourseType[] {
  return RECORDS.map((r) => ({
    id: r.id,
    title: r.title,
    instructor: ACADEMY_INSTRUCTOR_LINE,
    rating: r.rating,
    reviews: r.reviews,
    price: r.priceMain,
    level: r.level,
    duration: r.duration,
    image: r.image,
  }));
}

export function getCourseDetail(id: string): (CourseCatalogDetail & {
  instructor: {
    name: string;
    role: string;
    avatar: string;
    students: string;
    courses: number;
  };
  gstNote: string;
}) | null {
  const base = RECORDS.find((c) => c.id === id);
  if (!base) return null;
  return {
    ...base,
    gstNote: GST_PRICE_NOTE,
    instructor: {
      name: "Mont2 Faculty Pool",
      role: ACADEMY_INSTRUCTOR_LINE,
      avatar,
      students: "Working pros & students",
      courses: RECORDS.length,
    },
  };
}

export function listCourseRecords(): readonly CourseCatalogDetail[] {
  return RECORDS;
}
